import { z } from 'zod';

import { request } from '@/api/httpClient';
import type {
  ClientContactSummary,
  ClientContactInput,
  ClientDetails,
  ClientNoteSummary,
  ClientSummary,
  ServiceLocationSummary,
} from '@/features/clients/types/Client';

const clientSummarySchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  created_at: z.string().min(1),
});

const clientListSchema = z.array(clientSummarySchema);

const clientContactSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  home_phone: z.string().nullable().optional(),
  notify_by_email: z.union([z.boolean(), z.literal(0), z.literal(1)]).optional(),
  notify_by_sms: z.union([z.boolean(), z.literal(0), z.literal(1)]).optional(),
});

export async function searchClients(query = ''): Promise<ClientSummary[]> {
  const response = await request<unknown>('/managers/clients/search', {
    method: 'POST',
    body: query.trim() ? { q: query.trim() } : {},
  });

  return clientListSchema.parse(response).map((client) => ({
    id: client.id,
    name: client.name,
    createdAt: client.created_at,
  }));
}

export async function getClientContacts(clientId: number): Promise<ClientContactSummary[]> {
  const response = await request<unknown>(`/managers/clients/${clientId}/contacts`);

  return z
    .array(clientContactSchema)
    .parse(response)
    .map((contact) => ({
      id: contact.id,
      name: contact.name,
      email: contact.email ?? null,
      phone: contact.phone ?? null,
      homePhone: contact.home_phone ?? null,
      notifyByEmail: Boolean(contact.notify_by_email),
      notifyBySms: Boolean(contact.notify_by_sms),
    }));
}

function contactBody(values: ClientContactInput) {
  return {
    name: values.name,
    email: values.email || null,
    phone: values.phone || null,
    home_phone: values.homePhone || null,
    notify_by_email: values.notifyByEmail,
    notify_by_sms: values.notifyBySms,
  };
}

export async function createClientContact(clientId: number, values: ClientContactInput) {
  return clientContactSchema.parse(await request<unknown>(`/managers/clients/${clientId}/contacts`, {
    method: 'POST',
    body: contactBody(values),
  }));
}

export async function updateClientContact(
  clientId: number,
  contactId: number,
  values: ClientContactInput,
) {
  return clientContactSchema.parse(await request<unknown>(`/managers/clients/${clientId}/contacts/${contactId}`, {
    method: 'PUT',
    body: contactBody(values),
  }));
}

export async function deleteClientContact(clientId: number, contactId: number) {
  await request(`/managers/clients/${clientId}/contacts/${contactId}`, { method: 'DELETE' });
}

export async function deleteClient(id: number): Promise<void> {
  await request(`/managers/clients/${id}`, { method: 'DELETE' });
}

export async function createClient(values: {
  name: string;
  billingAddress: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  primaryContactHomePhone?: string;
}): Promise<number> {
  const response = await request<unknown>('/managers/clients', {
    method: 'POST',
    body: {
      name: values.name,
      billing_address: values.billingAddress || null,
      primary_contact_name: values.primaryContactName || null,
      primary_contact_email: values.primaryContactEmail || null,
      primary_contact_phone: values.primaryContactPhone || null,
      primary_contact_home_phone: values.primaryContactHomePhone || null,
    },
  });
  return z.object({ id: z.number().int().positive() }).parse(response).id;
}

export async function getClient(id: number): Promise<ClientDetails> {
  const response = await request<unknown>(`/managers/clients/${id}`);
  const client = clientSummarySchema
    .extend({
      billing_address: z.string().nullable().optional(),
      primary_contact_name: z.string().nullable().optional(),
      primary_contact_email: z.string().nullable().optional(),
      primary_contact_phone: z.string().nullable().optional(),
      primary_contact_home_phone: z.string().nullable().optional(),
    })
    .parse(response);
  return {
    id: client.id,
    name: client.name,
    createdAt: client.created_at,
    billingAddress: client.billing_address ?? null,
    primaryContactName: client.primary_contact_name ?? null,
    primaryContactEmail: client.primary_contact_email ?? null,
    primaryContactPhone: client.primary_contact_phone ?? null,
    primaryContactHomePhone: client.primary_contact_home_phone ?? null,
  };
}

export async function updateClient(
  id: number,
  values: {
    name: string;
    billingAddress: string | null;
    primaryContactName: string | null;
    primaryContactEmail: string | null;
    primaryContactPhone: string | null;
    primaryContactHomePhone: string | null;
  },
): Promise<void> {
  await request(`/managers/clients/${id}`, {
    method: 'PUT',
    body: {
      name: values.name,
      billing_address: values.billingAddress,
      primary_contact_name: values.primaryContactName,
      primary_contact_email: values.primaryContactEmail,
      primary_contact_phone: values.primaryContactPhone,
      primary_contact_home_phone: values.primaryContactHomePhone,
    },
  });
}

export async function getClientServiceLocations(id: number): Promise<ServiceLocationSummary[]> {
  const response = await request<unknown>(`/managers/clients/${id}/service-locations`);
  return z
    .array(
      z.object({
        id: z.number().int().positive(),
        name: z.string().min(1),
        gate_code: z.string().nullable().optional(),
      }),
    )
    .parse(response)
    .map((location) => ({
      id: location.id,
      name: location.name,
      gateCode: location.gate_code ?? null,
    }));
}

export interface ServiceLocationInput {
  name: string;
  gateCode: string;
  streetNumber: string;
  route: string;
  locality: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

export async function createClientServiceLocation(
  clientId: number,
  value: ServiceLocationInput,
): Promise<number> {
  const response = await request<unknown>(`/managers/clients/${clientId}/service-locations`, {
    method: 'POST',
    body: serviceLocationBody(value),
  });
  return z.object({ id: z.number().int().positive() }).parse(response).id;
}

function serviceLocationBody(value: ServiceLocationInput) {
  return {
    name: value.name,
    gate_code: value.gateCode || null,
    address: {
      street_number: value.streetNumber || null,
      route: value.route || null,
      locality: value.locality || null,
      postal_code: value.postalCode || null,
      latitude: value.latitude,
      longitude: value.longitude,
    },
  };
}

export async function getClientNotes(id: number): Promise<ClientNoteSummary[]> {
  const response = await request<unknown>(`/managers/clients/${id}/notes`);
  return z
    .array(
      z.object({
        id: z.number().int().positive(),
        note: z.string(),
        created_at: z.string(),
        user: z.object({ name: z.string() }).nullable().optional(),
      }),
    )
    .parse(response)
    .map((note) => ({
      id: note.id,
      note: note.note,
      createdAt: note.created_at,
      author: note.user?.name ?? 'Unknown',
    }));
}

const timelineEntrySchema = z.object({
  id: z.number().int().positive(),
  note: z.string().nullable(),
  related_url: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  related_id: z.number().int().nullable().optional(),
  created_at: z.string(),
});
export type TimelineEntry = z.infer<typeof timelineEntrySchema>;
export async function getClientTimeline(id: number): Promise<TimelineEntry[]> {
  const response = await request<unknown>(`/managers/clients/${id}/timeline-entries/search`);
  return z.object({ data: z.array(timelineEntrySchema) }).parse(response).data;
}

export async function createClientNote(clientId: number, note: string): Promise<void> {
  await request(`/managers/clients/${clientId}/notes`, { method: 'POST', body: { note } });
}

export async function updateClientNote(
  clientId: number,
  noteId: number,
  note: string,
): Promise<void> {
  await request(`/managers/clients/${clientId}/notes/${noteId}`, {
    method: 'PUT',
    body: { note },
  });
}

export async function deleteClientNote(clientId: number, noteId: number): Promise<void> {
  await request(`/managers/clients/${clientId}/notes/${noteId}`, { method: 'DELETE' });
}
